import type {
  Asset,
  AutoEditParams,
  Composition,
  CreateJobRequest,
  Job,
  JobKind,
  Overlay,
  OutputSpec,
  Project,
  ProjectDetail,
  Scene,
  Segment,
} from '@shared/types'
import { SECTION_COLOR_ORDER } from '@shared/colorGrades'
import type { ApiClient, OverlayRenderResult, UploadProgress } from './types'
import {
  MOCK_ASSETS,
  MOCK_ASSETS_2,
  MOCK_AUDIO_LIBRARY,
  MOCK_COMPOSITION,
  MOCK_COMPOSITION_2A,
  MOCK_COMPOSITION_2B,
  MOCK_COMPOSITION_CUSTOM,
  MOCK_PROJECT,
  MOCK_PROJECT_2,
  MOCK_PROJECT_3,
  MOCK_RENDER_JOBS,
  makeEmptyComposition,
} from './mockFixtures'
import { makeId } from '../lib/id'

/** In-memory mock backend. Lives for the tab's lifetime; good enough for UI dev/E2E. */
const projects = new Map<string, Project>([
  [MOCK_PROJECT.id, MOCK_PROJECT],
  [MOCK_PROJECT_2.id, MOCK_PROJECT_2],
  // P10-3 担当B (出力サイズ切替): mockFixtures.ts's MOCK_PROJECT_3 doc comment.
  [MOCK_PROJECT_3.id, MOCK_PROJECT_3],
])
const assets = new Map<string, Asset>([...MOCK_ASSETS, ...MOCK_ASSETS_2].map((a) => [a.id, a]))
const compositions = new Map<string, Composition>([
  [MOCK_COMPOSITION.id, MOCK_COMPOSITION],
  [MOCK_COMPOSITION_2A.id, MOCK_COMPOSITION_2A],
  [MOCK_COMPOSITION_2B.id, MOCK_COMPOSITION_2B],
  [MOCK_COMPOSITION_CUSTOM.id, MOCK_COMPOSITION_CUSTOM],
])
const jobs = new Map<string, Job>()

// P10-2 (task spec §3 item 12 / mockFixtures.ts's MOCK_COMPOSITION_2B doc
// comment): a permanently 'running' render Job, seeded directly (not via
// `runFakeJob`'s timers) so e2e can hit the 409/"disabled" busy paths on
// MOCK_COMPOSITION_2B deterministically, with no timing race.
const BUSY_JOB_ID = makeId('job')
jobs.set(BUSY_JOB_ID, {
  id: BUSY_JOB_ID,
  kind: 'render',
  status: 'running',
  progress: 0.4,
  log: ['render job queued (mock, pinned running for e2e)'],
  createdAt: new Date(Date.now() - 1000 * 30).toISOString(),
  updatedAt: new Date(Date.now() - 1000 * 5).toISOString(),
  compositionId: MOCK_COMPOSITION_2B.id,
})

// P10-3 担当A (書き出し履歴): mockFixtures.ts's MOCK_RENDER_JOBS doc comment.
for (const job of MOCK_RENDER_JOBS) jobs.set(job.id, job)

/** `.status` on the thrown Error, same convention `httpClient.ts`'s `req()` uses for every non-2xx response. */
function mockHttpError(status: number, message: string): Error & { status: number } {
  const err = new Error(message) as Error & { status: number }
  err.status = status
  return err
}

const SOURCE_VIDEOS = ['asset-1', 'asset-2', 'asset-3'] as const
let uploadCounter = 0

function nowIso() {
  return new Date().toISOString()
}

function updateJob(id: string, patch: Partial<Job>) {
  const j = jobs.get(id)
  if (!j) return
  jobs.set(id, { ...j, ...patch, updatedAt: nowIso() })
}

function runFakeJob(kind: JobKind, opts: { totalMs: number; onDone: () => void }): Job {
  const id = makeId('job')
  const job: Job = {
    id,
    kind,
    status: 'queued',
    progress: 0,
    log: [`${kind} job queued (mock)`],
    createdAt: nowIso(),
    updatedAt: nowIso(),
  }
  jobs.set(id, job)
  const ticks = 12
  const stepMs = opts.totalMs / ticks
  let i = 0
  setTimeout(() => {
    updateJob(id, { status: 'running', log: [...(jobs.get(id)?.log ?? []), `${kind} started`] })
    const timer = setInterval(() => {
      i += 1
      const progress = Math.min(1, i / ticks)
      if (progress >= 1) {
        clearInterval(timer)
        updateJob(id, {
          status: 'done',
          progress: 1,
          log: [...(jobs.get(id)?.log ?? []), `${kind} complete`],
        })
        opts.onDone()
      } else {
        updateJob(id, { progress })
      }
    }, stepMs)
  }, 120)
  return job
}

function scenesFor(assetId: string, durationSec: number, count = 5): Scene[] {
  const step = durationSec / count
  return Array.from({ length: count }, (_, i) => ({
    index: i,
    startSec: Number((i * step).toFixed(2)),
    endSec: Number(((i + 1) * step).toFixed(2)),
    thumbPath: `/mock/scenes/${assetId}-${i}.jpg`,
  }))
}

export const mockClient: ApiClient = {
  async listProjects() {
    return Array.from(projects.values())
  },

  async createProject(name) {
    const p: Project = {
      id: makeId('proj'),
      name,
      createdAt: nowIso(),
      updatedAt: nowIso(),
      assetIds: [],
      compositionIds: [],
    }
    projects.set(p.id, p)
    return p
  },

  async getProject(id) {
    const project = projects.get(id)
    if (!project) throw new Error(`project ${id} not found (mock)`)
    const projAssets = project.assetIds.map((aid) => assets.get(aid)).filter(Boolean) as Asset[]
    const projComps = project.compositionIds
      .map((cid) => compositions.get(cid))
      .filter(Boolean) as Composition[]
    return { project, assets: projAssets, compositions: projComps }
  },

  async uploadAssets(projectId, files, onProgress) {
    const project = projects.get(projectId)
    const createdJobs: Job[] = []
    for (const file of files) {
      // simulate xhr upload progress first
      const total = file.size || 5_000_000
      let loaded = 0
      await new Promise<void>((resolve) => {
        const steps = 8
        let s = 0
        const t = setInterval(() => {
          s += 1
          loaded = Math.round((total * s) / steps)
          onProgress?.({ fileName: file.name, loaded, total })
          if (s >= steps) {
            clearInterval(t)
            resolve()
          }
        }, 60)
      })

      const source = SOURCE_VIDEOS[uploadCounter % SOURCE_VIDEOS.length]
      uploadCounter += 1
      const assetId = makeId('asset')
      const duration = source === 'asset-3' ? 9 : source === 'asset-2' ? 7 : 8
      const pendingAsset: Asset = {
        id: assetId,
        projectId,
        filename: file.name,
        mime: file.type || 'video/mp4',
        bytes: total,
        durationSec: duration,
        width: 540,
        height: 960,
        fps: 30,
        hasAudio: false,
        createdAt: nowIso(),
        originalPath: `/mock/proxies/${source}.mp4`,
        proxyPath: null,
        thumbPath: null,
        scenes: [],
        ingestStatus: 'processing',
      }
      assets.set(assetId, pendingAsset)
      if (project) project.assetIds.push(assetId)

      const job = runFakeJob('ingest', {
        totalMs: 1400,
        onDone: () => {
          const a = assets.get(assetId)
          if (!a) return
          assets.set(assetId, {
            ...a,
            proxyPath: `/mock/proxies/${source}.mp4`,
            thumbPath: `/mock/thumbs/${source}.jpg`,
            scenes: scenesFor(source, duration),
            ingestStatus: 'ready',
          })
        },
      })
      job.assetId = assetId
      jobs.set(job.id, job)
      createdJobs.push(job)
    }
    return createdJobs
  },

  async getAsset(id) {
    const a = assets.get(id)
    if (!a) throw new Error(`asset ${id} not found (mock)`)
    return a
  },

  async saveComposition(comp) {
    const existing = compositions.get(comp.id)
    const next: Composition = {
      ...comp,
      version: (existing?.version ?? 0) + 1,
      updatedAt: nowIso(),
    }
    compositions.set(next.id, next)
    const project = projects.get(comp.projectId)
    if (project && !project.compositionIds.includes(next.id)) {
      project.compositionIds.push(next.id)
    }
    return next
  },

  async getComposition(id) {
    const c = compositions.get(id)
    if (!c) throw new Error(`composition ${id} not found (mock)`)
    return c
  },

  async autoEdit(compositionId, params: AutoEditParams) {
    const base = compositions.get(compositionId) ?? makeEmptyComposition(params.projectId, '自動編集')
    const readyAssets = params.assets.filter((a) => a.ingestStatus === 'ready' && a.scenes.length)
    const sections = params.sections.map((s, i) => ({
      id: makeId('sec'),
      name: s.name,
      nameEn: s.nameEn,
      color: s.color ?? SECTION_COLOR_ORDER[i % SECTION_COLOR_ORDER.length],
    }))

    const segments: Segment[] = []
    let t = 0
    let assetCursor = 0
    let guard = 0
    const shotLen = params.rhythm === 'brisk' ? 1.1 : params.rhythm === 'calm' ? 2.2 : 1.6
    while (t < params.targetDurationSec && readyAssets.length && guard < 200) {
      guard += 1
      const asset = readyAssets[assetCursor % readyAssets.length]
      assetCursor += 1
      const scene = asset.scenes[guard % asset.scenes.length]
      if (!scene) continue
      const dur = Math.min(shotLen, scene.endSec - scene.startSec)
      if (dur < 0.4) continue
      segments.push({
        id: makeId('seg'),
        assetId: asset.id,
        inSec: scene.startSec,
        outSec: scene.startSec + dur,
        speed: 1,
        sectionId: sections.length ? sections[segments.length % sections.length].id : null,
        fit: 'cover',
        cropX: 0.5,
        transitionIn: segments.length === 0 ? 'cut' : 'fade',
      })
      t += dur
    }

    const overlays: Composition['tracks']['overlays'] = [
      {
        kind: 'title',
        id: makeId('ov'),
        startSec: 0,
        durationSec: 2,
        text: params.title,
        emphasis: params.titleEmphasis,
        vertical: true,
        seasonBadge: params.seasonBadge,
        dateText: params.dateText,
        venue: params.venue,
        position: 'center',
      },
    ]
    sections.forEach((sec, i) => {
      const segAt = segments[i * Math.floor(segments.length / Math.max(1, sections.length))]
      if (segAt) {
        let cursor = 0
        for (const s of segments) {
          if (s.id === segAt.id) break
          cursor += (s.outSec - s.inSec) / s.speed
        }
        overlays.push({
          kind: 'label',
          id: makeId('ov'),
          startSec: cursor,
          durationSec: 1.5,
          ja: sec.name,
          en: sec.nameEn,
          style: 'bracket',
          position: 'left-upper',
        })
      }
    })
    overlays.push({
      kind: 'end',
      id: makeId('ov'),
      startSec: Math.max(0, t - 2),
      durationSec: 2,
      lines: params.endCardLines,
      vertical: true,
      cta: params.cta,
      venue: params.venue,
    })

    const audio: Composition['tracks']['audio'] = params.bgm
      ? [
          {
            id: makeId('aud'),
            kind: 'bgm',
            assetId: params.bgm.assetId,
            builtinId: params.bgm.builtinId,
            startSec: 0,
            inSec: 0,
            outSec: null,
            gainDb: params.bgm.gainDb ?? -8,
            fadeInSec: 0.5,
            fadeOutSec: 1.2,
          },
        ]
      : []

    // REVIEW_v1.md M7: mirrors the real server (engine/src/autoEdit.ts) —
    // `autoEdit` = AutoEditParams minus assets/projectId — so mock mode
    // exercises the same "settings survive a reload" path TabTemplate.tsx
    // relies on.
    const { assets: _assets, projectId: _projectId, ...autoEditSettings } = params
    const result: Composition = {
      ...base,
      template: params.template,
      seed: params.seed ?? base.seed,
      sections,
      tracks: { video: segments, overlays, audio },
      meta: {
        ...base.meta,
        title: params.title,
        seasonBadge: params.seasonBadge,
        venue: params.venue,
        dateText: params.dateText,
      },
      autoEdit: { ...autoEditSettings, seed: params.seed ?? base.seed },
    }

    // Wrapped in {composition, warnings, stats} to match the real server's
    // POST /api/compositions/:id/auto response shape (server/src/routes/
    // compositions.ts, task spec S5 item 1) — mock mode never surfaces a
    // different contract than the real API to the rest of the app.
    const warnings: string[] = []
    if (readyAssets.length === 0) {
      warnings.push('ingestStatus=readyのassetが1件も無く、Segmentを生成できませんでした。(mock)')
    }
    const shotLens = segments.map((s) => (s.outSec - s.inSec) / s.speed)
    return {
      composition: result,
      warnings,
      stats: {
        segments: segments.length,
        totalSec: t,
        shotLenMedian: shotLens.length ? shotLens.sort((a, b) => a - b)[Math.floor(shotLens.length / 2)] : 0,
        // mock mode doesn't run the real hue-assignment DP (engine/src/
        // templates/common.ts) — `null` honestly says "not computed" rather
        // than fabricating a number, same convention the real engine uses
        // when there's no chromatic data to score.
        hueMatch: null,
        perSection: [],
      },
    }
  },

  async renderOverlay(
    _overlay: Overlay,
    _output: OutputSpec,
    _opts?: { hideEmblem?: boolean },
  ): Promise<OverlayRenderResult> {
    // No render server in mock mode: exercise the same 501 fallback path the
    // real client hits while S2 hasn't shipped /api/overlays/render yet.
    return { ok: false, reason: 'not_implemented' }
  },

  async createJob(req: CreateJobRequest) {
    const isPreview = req.kind === 'preview'
    const job = runFakeJob(req.kind, {
      totalMs: isPreview ? 1800 : 4200,
      onDone: () => {
        const source = SOURCE_VIDEOS[0]
        updateJob(job.id, { resultPath: `/mock/proxies/${source}.mp4` })
      },
    })
    job.compositionId = req.compositionId
    jobs.set(job.id, job)
    return job
  },

  async getJob(id) {
    const j = jobs.get(id)
    if (!j) throw new Error(`job ${id} not found (mock)`)
    return j
  },

  async listJobs(params: { compositionId?: string; kind?: JobKind }) {
    return Array.from(jobs.values())
      .filter((j) => !params.compositionId || j.compositionId === params.compositionId)
      .filter((j) => !params.kind || j.kind === params.kind)
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
  },

  async deleteProject(id) {
    const project = projects.get(id)
    if (!project) throw mockHttpError(404, `project ${id} not found (mock)`)

    const busy = Array.from(jobs.values()).filter(
      (j) =>
        j.compositionId !== undefined &&
        project.compositionIds.includes(j.compositionId) &&
        (j.status === 'queued' || j.status === 'running'),
    )
    if (busy.length > 0) {
      throw mockHttpError(409, `project_busy: ${busy.length} 件のジョブが進行中です (mock)`)
    }

    for (const compId of project.compositionIds) compositions.delete(compId)
    for (const assetId of project.assetIds) assets.delete(assetId)
    for (const [jobId, j] of jobs) {
      if (j.compositionId !== undefined && project.compositionIds.includes(j.compositionId)) jobs.delete(jobId)
    }
    projects.delete(id)
  },

  async renameProject(id, name) {
    const project = projects.get(id)
    if (!project) throw mockHttpError(404, `project ${id} not found (mock)`)
    const trimmed = name.trim()
    if (trimmed.length < 1 || trimmed.length > 80) {
      throw mockHttpError(400, 'validation_error: name must be 1..80 chars after trim (mock)')
    }
    const updated: Project = { ...project, name: trimmed, updatedAt: nowIso() }
    projects.set(id, updated)
    return updated
  },

  async deleteComposition(id) {
    const composition = compositions.get(id)
    if (!composition) throw mockHttpError(404, `composition ${id} not found (mock)`)

    const busy = Array.from(jobs.values()).filter(
      (j) => j.compositionId === id && (j.status === 'queued' || j.status === 'running'),
    )
    if (busy.length > 0) {
      throw mockHttpError(409, `composition_busy: ${busy.length} 件のジョブが進行中です (mock)`)
    }

    compositions.delete(id)
    for (const [jobId, j] of jobs) {
      if (j.compositionId === id) jobs.delete(jobId)
    }
    const project = projects.get(composition.projectId)
    if (project) {
      const idx = project.compositionIds.indexOf(id)
      if (idx !== -1) {
        project.compositionIds.splice(idx, 1)
        project.updatedAt = nowIso()
      }
    }
  },

  async duplicateComposition(id, name) {
    const source = compositions.get(id)
    if (!source) throw mockHttpError(404, `composition ${id} not found (mock)`)
    const trimmedName = name?.trim()
    const duplicate: Composition = {
      ...structuredClone(source),
      id: makeId('comp'),
      name: trimmedName || `${source.name} のコピー`,
      version: 1,
      updatedAt: nowIso(),
    }
    compositions.set(duplicate.id, duplicate)
    const project = projects.get(source.projectId)
    if (project) {
      project.compositionIds.push(duplicate.id)
      project.updatedAt = nowIso()
    }
    return duplicate
  },

  fileUrl(storageKey) {
    if (!storageKey) return ''
    return storageKey
  },

  async listAudioLibrary() {
    return MOCK_AUDIO_LIBRARY
  },

  // ---- P10-2 (docs/P10_PHASE2.md §2, 担当A: 素材の取り違え修正＋素材ビン管理) ----

  async patchAssetLabel(id, label) {
    const a = assets.get(id)
    if (!a) throw mockHttpError(404, `asset ${id} not found (mock)`)
    const trimmed = label.trim()
    if (trimmed.length > 80) {
      throw mockHttpError(400, 'validation_error: label must be 1..80 chars after trim (mock)')
    }
    if (trimmed.length === 0) {
      // mirrors the server's `delete asset.label` — the key itself must be
      // absent, not merely `undefined`, so `assetDisplayName`'s `??`
      // fallback to `filename` is exercised the same way either client hits.
      const { label: _dropped, ...rest } = a
      const updated = rest as Asset
      assets.set(id, updated)
      return updated
    }
    const updated: Asset = { ...a, label: trimmed }
    assets.set(id, updated)
    return updated
  },

  async deleteAsset(id) {
    const a = assets.get(id)
    if (!a) throw mockHttpError(404, `asset ${id} not found (mock)`)

    const project = projects.get(a.projectId)
    if (project) {
      const usedBy = project.compositionIds
        .map((cid) => compositions.get(cid))
        .filter((c): c is Composition => !!c)
        .filter(
          (c) => c.tracks.video.some((s) => s.assetId === id) || c.tracks.audio.some((clip) => clip.assetId === id),
        )
      if (usedBy.length > 0) {
        throw mockHttpError(
          409,
          `asset_in_use: 構成案『${usedBy.map((c) => c.name).join('』『')}』で使用中のため削除できません (mock)`,
        )
      }
    }

    const busy = Array.from(jobs.values()).some(
      (j) => j.assetId === id && (j.kind === 'ingest' || j.kind === 'redetect') && (j.status === 'queued' || j.status === 'running'),
    )
    if (busy) throw mockHttpError(409, 'asset_busy: この素材は処理中です (mock)')

    assets.delete(id)
    if (project) {
      const idx = project.assetIds.indexOf(id)
      if (idx !== -1) project.assetIds.splice(idx, 1)
    }
    for (const [jobId, j] of jobs) {
      if (j.assetId === id) jobs.delete(jobId)
    }
  },

  async redetectAsset(id, threshold) {
    const a = assets.get(id)
    if (!a) throw mockHttpError(404, `asset ${id} not found (mock)`)
    const busy = Array.from(jobs.values()).some(
      (j) => j.assetId === id && (j.kind === 'ingest' || j.kind === 'redetect') && (j.status === 'queued' || j.status === 'running'),
    )
    if (busy) throw mockHttpError(409, 'asset_busy: この素材は処理中です (mock)')

    const job = runFakeJob('redetect', {
      totalMs: 600,
      onDone: () => {
        const cur = assets.get(id)
        if (!cur) return
        // Deterministic scene-count CHANGE so e2e can assert the strip
        // updated — real behavior only fixes the count once ffmpeg actually
        // re-runs, mock mode just needs a visibly different number. Roughly
        // follows the same direction as the real detector (higher threshold
        // -> fewer cuts) without pretending to be exact.
        const newCount = threshold >= 0.5 ? Math.max(2, cur.scenes.length - 2) : cur.scenes.length + 1
        assets.set(id, { ...cur, scenes: scenesFor(id, cur.durationSec, newCount) })
      },
    })
    job.assetId = id
    jobs.set(job.id, job)
    return job
  },

  // ---- P10-3 (docs/P10_PHASE3.md §2, 担当A: 書き出し履歴) ----

  jobDownloadUrl(jobId) {
    // No real download endpoint in mock mode — a stable, harmless anchor
    // target (never a fetchable URL) that export-history.spec.ts's href
    // assertion and the `<a download>` element itself are both fine with.
    return `#mock-download-${jobId}`
  },

  async deleteJob(jobId) {
    const job = jobs.get(jobId)
    if (!job) throw mockHttpError(404, `job ${jobId} not found (mock)`)
    if (job.kind !== 'render' && job.kind !== 'preview') {
      throw mockHttpError(409, `job_kind: job kind "${job.kind}" cannot be deleted here (mock)`)
    }
    if (job.status === 'queued' || job.status === 'running') {
      throw mockHttpError(409, 'job_running: このジョブはまだ処理中です (mock)')
    }
    jobs.delete(jobId)
  },

  async listProjectJobs(projectId, kind) {
    const project = projects.get(projectId)
    if (!project) throw mockHttpError(404, `project ${projectId} not found (mock)`)
    return Array.from(jobs.values())
      .filter((j) => j.compositionId !== undefined && project.compositionIds.includes(j.compositionId))
      .filter((j) => !kind || j.kind === kind)
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
  },
}
